import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import { useSnackbar } from "notistack";

import Page from "../../components/Page";
import FormDialog from "../../components/common/FormDialog";
import DelegateForm from "../Delegates/DelegateForm";
import { usePGIMode } from "../../Hooks/PGIModeContext";
import { createDelegate } from "../../DAL/Delegate";

export default function Dashboard() {
  const { metaTitle } = usePGIMode();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formDialog, setFormDialog] = useState({ open: false, delegate: null });

  const handleOpenAddDelegate = () => {
    setFormDialog({ open: true, delegate: null });
  };

  const handleCloseForm = () => {
    setFormDialog({ open: false, delegate: null });
  };

  const handleAddDelegate = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      const data = formDialog.delegate;

      formData.append("first_name", data.first_name);
      formData.append("last_name", data.last_name || "");
      formData.append("email", data.email);
      if (data.password) formData.append("password", data.password);
      formData.append("contact_number", data.contact_number || "");
      formData.append("address", data.address || "");
      formData.append("city", data.city || "");
      formData.append("state", data.state || "");
      formData.append("main_heading", data.main_heading || "");
      formData.append("biography", data.biography || "");
      formData.append("status", data.status);
      formData.append("is_2f_enabled", data.is_2f_enabled);
      formData.append("time_zone", "Europe/London");

      if (data.image instanceof File) {
        formData.append("image", data.image);
      }
console.log("Form Data:", ...formData);
      const response = await createDelegate(formData);

      if (response.code === 200) {
        enqueueSnackbar(response.message, { variant: "success" });
        handleCloseForm();
        navigate("/coaches");
      } else {
        enqueueSnackbar(response.message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error saving coach:", error);
      enqueueSnackbar("Error saving coach", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const howToAddSteps = [
    "Every coach has a unique login url.",
    "Click the 'Add Coach' button on this dashboard or the coaches page.",
    "Fill in the required personal information (Name, Email, Password).",
    "Upload a profile image for better identification.",
    "Set the initial status and provide professional biography details.",
  ];

  return (
    <Page title={`Dashboard - ${metaTitle}`}>
      <Box sx={{ mx: "auto", px: 3, py: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 5 }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            Dashboard
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Icon icon={plusFill} />}
              onClick={handleOpenAddDelegate}
              sx={{
                px: 3,
                width: { xs: "100%", sm: "auto" },
                height: 42,
              }}
            >
              Add New Coach
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                height: "100%",
                boxShadow: (theme) => theme.customShadows.z8,
              }}
            >
              <CardContent sx={{ p: 4, bgcolor: "primary.lighter" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 3 }}
                >
                  <Icon
                    icon={plusFill}
                    width={24}
                    height={24}
                    color="primary.main"
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    How to Add a Coach?
                  </Typography>
                </Stack>
                <List>
                  {howToAddSteps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary.lighter",
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={step}
                        primaryTypographyProps={{
                          variant: "body1",
                          color: "text.secondary",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <FormDialog
          open={formDialog.open}
          onClose={handleCloseForm}
          title="Add New Coach"
          submitText="Add Coach"
          loading={loading}
          onSubmit={handleAddDelegate}
        >
          <DelegateForm
            delegate={formDialog.delegate}
            onChange={(newData) =>
              setFormDialog((prev) => ({ ...prev, delegate: newData }))
            }
          />
        </FormDialog>
      </Box>
    </Page>
  );
}
